import { AuthProvider } from '@avanzu/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GoogleIcon from '@mui/icons-material/Google';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

interface ProviderCardProps {
  email?: string;
  provider: AuthProvider;
  isConnected: boolean;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

const ProviderCard = ({
  email,
  provider,
  isConnected,
  isConnecting,
  onConnect,
  onDisconnect,
}: ProviderCardProps) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main',
        },
        ...(isConnected && {
          borderColor: 'success.main',
          boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)',
        }),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
              <Avatar
                sx={{
                  bgcolor: isConnected ? 'success.light' : 'grey.100',
                  width: 48,
                  height: 48,
                }}
              >
                <ProviderAvatar provider={provider} isConnected={isConnected} />
              </Avatar>
              <Stack spacing={0} flex={1}>
                <Typography variant="h6" component="h3">
                  {capitalise(provider)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {isConnected
                    ? `Connected as ${email}`
                    : `Connect your ${capitalise(provider)} account for single sign-on`}
                </Typography>
              </Stack>
              {isConnected && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label="Connected"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          {/* Action Button */}
          <Box>
            {isConnected ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={isConnecting}
                sx={{ minWidth: 80 }}
                onClick={onDisconnect}
              >
                Unlink
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isConnecting}
                sx={{ minWidth: 80 }}
                onClick={onConnect}
                startIcon={
                  isConnecting ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProviderCard;

export const ProviderAvatar = ({
  provider,
  isConnected,
  size = 48,
}: {
  provider: AuthProvider;
  isConnected: boolean;
  size?: number;
}) => {
  switch (provider) {
    case AuthProvider.GOOGLE:
      return <GoogleIcon sx={{ color: isConnected ? 'success.dark' : 'grey.600' }} />;
    case AuthProvider.XERO:
      return (
        // <Typography variant="body1" sx={{ color: isConnected ? 'success.dark' : 'grey.600' }}>
        //   X
        // </Typography>
        <img src="/images/xero-logo.svg" alt="Xero" style={{ width: size, height: size }} />
      );

    // Add cases for other providers as needed
    default:
      return <Typography variant="body1">U</Typography>;
  }
};

const capitalise = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);
